import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { Space, Tree as BaseTree, Input } from 'antd';
import { Box } from '../box';
import { useHeight } from '../../hooks';
import { Spin } from '../spin';
import { useExpand, useTitleRender, useTreeData } from './hooks';
import { TreeService } from './service';
import styles from './style.module.less';

import type { DataSource, TreeProps, TreeRef } from './types';
import type { CSSProperties, ReactElement, Ref } from 'react';

function TreeV2Component<T extends DataSource>(props: TreeProps<T>, ref: Ref<TreeRef>): ReactElement {
  const {
    value,
    title,
    dataSource,
    expandRoot = true,
    loading = false,
    search = true,
    loadingTips = '',
    searchPlaceholder = '',
    blockNode = true,
    defaultExpandedKeys,
    onSearchChange,
    className
  } = props;

  const { current: service } = useRef(new TreeService());

  const [searchValue, setSearchValue] = useState('');

  // 受控展开的节点
  const { expandedKeys, onExpand, autoExpandParent } = useExpand(
    service,
    dataSource,
    expandRoot,
    searchValue,
    search,
    defaultExpandedKeys
  );

  const treeData = useTreeData(service, dataSource);

  const { titleRender } = useTitleRender(searchValue);

  const height = useHeight(`.${styles.tree as string}`);
  const loadingHeight = useHeight(`.${styles.loadingBox as string}`);
  const loadingStyle: CSSProperties = { height: loadingHeight };

  // 为外部组件提供检索功能
  useImperativeHandle(ref, () => ({
    search: (keyword: string): void => {
      setSearchValue(keyword.trim());
    }
  }));

  if (loading) {
    return (<Box className={styles.loadingBox} style={loadingStyle}>
      <Spin tip={loadingTips} className={styles.treeLoading} />
    </Box>
    );
  }

  return (
    <Space direction='vertical' className={styles.treeContent}>
      {title != null && <Box className={styles.title}>{title}</Box>}
      {search
      && <Input.Search
        className={styles.treeSearch}
        value={searchValue}
        placeholder={searchPlaceholder}
        onChange={event => {
          setSearchValue(event.target.value.trim());
          onSearchChange?.(event.target.value.trim());
        }}
      />
      }
      <BaseTree
        className={className ?? styles.tree}
        selectedKeys={value}
        showLine={true}
        showIcon={true}
        height={height}
        autoExpandParent={autoExpandParent}
        blockNode={blockNode ?? true}
        expandedKeys={expandedKeys}
        treeData={treeData}
        onExpand={onExpand}
        onSelect={(selectedKeys, info): void => {
          props.onChange?.(selectedKeys.map(key => `${key}`), info);
        }}
        titleRender={titleRender}
      />
    </Space>
  );
}

export const TreeV2 = forwardRef<TreeRef, TreeProps<DataSource>>(TreeV2Component);
